/**
 * 관리자 접근 제어(IP·국가 차단) 페이지 전용 초기화.
 *
 * 1) 차단 토글(IP/국가) → ToggleSwitchHandler 로 비동기 처리(POST 플립, { result, msg, enabled }).
 * 2) 추가 폼(IP/국가) → 자체 fetch 처리. 서버가 self-lockout warning 을 실으면 Modal 로 강조한 뒤
 *    redirect 로 페이지를 리로드한다(공통 AdminForm 은 warning 을 다루지 않으므로 여기서 처리).
 *
 * 추가 폼은 data-admin-ajax 를 쓰지 않는다(중복 바인딩 방지 — 이 핸들러가 단독 소유).
 * 뷰: resources/views/admin/pages/access-control/index.php
 */
import { ToggleSwitchHandler } from "../components/ToggleSwitchHandler";
import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

const ROOT = ".admin-access-control";
const ADD_FORM = "form[data-ac-add]";

interface AddResult {
  result: "success" | "fail";
  msg?: string;
  warning?: string;
  redirect?: string;
}

export function initAccessControl(): void {
  const root = document.querySelector<HTMLElement>(ROOT);
  if (!root) return;

  // 1) 토글 — 페이지 새로고침 없이 상태 전환
  ToggleSwitchHandler.init(root);

  // 2) 추가 폼 — warning 처리 포함
  root.querySelectorAll<HTMLFormElement>(ADD_FORM).forEach((form) => {
    if (form.dataset.acBound === "1") return;
    form.dataset.acBound = "1";
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      // IP 폼은 옥텟/텍스트칸을 hidden ip_cidr 로 조립한 뒤 전송.
      if (form.dataset.acAdd === "ip") {
        assembleIpCidr(form);
      }
      void submitAddForm(form);
    });
  });

  // 3) "내 현재 IP 입력" — 버튼의 data-ac-ip-fill 값을 IP 입력칸(data-ac-ip-field)에 채운다.
  root.querySelectorAll<HTMLButtonElement>("[data-ac-ip-fill]").forEach((btn) => {
    if (btn.dataset.acFillBound === "1") return;
    btn.dataset.acFillBound = "1";
    btn.addEventListener("click", () => {
      const ip = btn.dataset.acIpFill ?? "";
      const field = root.querySelector<HTMLInputElement>("[data-ac-ip-field]");
      if (field && ip !== "") {
        field.value = ip;
        field.focus();
      }
    });
  });

  // 4) "복사" — 상태 패널의 현재 IP 를 클립보드로 복사.
  root.querySelectorAll<HTMLButtonElement>("[data-ac-copy]").forEach((btn) => {
    if (btn.dataset.acCopyBound === "1") return;
    btn.dataset.acCopyBound = "1";
    btn.addEventListener("click", () => {
      const ip = btn.dataset.acCopy ?? "";
      if (ip === "") return;
      void navigator.clipboard.writeText(ip).then(
        () => Toast.success(`${ip} 복사됨`),
        () => Toast.error("복사에 실패했습니다."),
      );
    });
  });

  // 5) IPv4 옥텟 4박스 — 숫자만·0~255 클램프·자동 다음칸·붙여넣기 분배.
  initOctets(root);
}

/** 옥텟 박스 그룹의 입력 UX 를 바인딩한다. */
function initOctets(root: HTMLElement): void {
  const group = root.querySelector<HTMLElement>("[data-ac-octets]");
  if (!group || group.dataset.acOctetsBound === "1") return;
  group.dataset.acOctetsBound = "1";

  const boxes = Array.from(group.querySelectorAll<HTMLInputElement>("[data-ac-octet]"));

  const sanitize = (v: string): string => {
    const digits = v.replace(/\D/g, "").slice(0, 3);
    if (digits === "") return "";
    const n = Math.min(255, parseInt(digits, 10));
    return String(n);
  };

  boxes.forEach((box, idx) => {
    box.addEventListener("input", () => {
      const before = box.value;
      box.value = sanitize(before);
      // 3자리이거나 255 도달이면 다음 칸으로 자동 이동.
      if (box.value.length === 3 && idx < boxes.length - 1) {
        boxes[idx + 1].focus();
        boxes[idx + 1].select();
      }
    });
    box.addEventListener("keydown", (e) => {
      if (e.key === "." || e.key === " ") {
        e.preventDefault();
        if (idx < boxes.length - 1) { boxes[idx + 1].focus(); boxes[idx + 1].select(); }
      } else if (e.key === "Backspace" && box.value === "" && idx > 0) {
        e.preventDefault();
        boxes[idx - 1].focus();
      } else if (e.key === "ArrowLeft" && box.selectionStart === 0 && idx > 0) {
        boxes[idx - 1].focus();
      } else if (e.key === "ArrowRight" && box.selectionStart === box.value.length && idx < boxes.length - 1) {
        boxes[idx + 1].focus();
      }
    });
    box.addEventListener("paste", (e) => {
      const text = e.clipboardData?.getData("text") ?? "";
      const parts = text.trim().split(".");
      if (parts.length === 4 && parts.every((p) => /^\d{1,3}$/.test(p))) {
        e.preventDefault();
        boxes.forEach((b, i) => { b.value = sanitize(parts[i]); });
        boxes[boxes.length - 1].focus();
      }
    });
  });
}

/** IP 폼 제출 직전 hidden ip_cidr 값을 결정. 텍스트칸(CIDR/IPv6) 우선, 없으면 옥텟 a.b.c.d. */
function assembleIpCidr(form: HTMLFormElement): void {
  const hidden = form.querySelector<HTMLInputElement>("[data-ac-ip-hidden]");
  if (!hidden) return;
  const text = form.querySelector<HTMLInputElement>("[data-ac-ip-field]");
  const typed = (text?.value ?? "").trim();
  if (typed !== "") {
    hidden.value = typed;
    return;
  }
  const octets = Array.from(form.querySelectorAll<HTMLInputElement>("[data-ac-octet]"))
    .map((b) => b.value.trim());
  if (octets.length === 4 && octets.every((o) => o !== "")) {
    hidden.value = octets.join(".");
  } else {
    hidden.value = ""; // 서버가 422 "IP 입력" 반환 (기존 동작)
  }
}

async function submitAddForm(form: HTMLFormElement): Promise<void> {
  const btn = form.querySelector<HTMLButtonElement>("button[type='submit']");
  const loader = form.querySelector<HTMLElement>(".admin-form__loader");
  if (btn) btn.disabled = true;
  if (loader) loader.style.display = "";

  const restore = (): void => {
    if (btn) btn.disabled = false;
    if (loader) loader.style.display = "none";
  };

  try {
    const res = await fetch(form.action, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
      credentials: "same-origin",
    });

    let data: AddResult;
    try {
      data = (await res.json()) as AddResult;
    } catch {
      restore();
      void Modal.alert("응답을 처리할 수 없습니다.");
      return;
    }

    if (data.result !== "success") {
      restore();
      void Modal.alert(data.msg || "추가에 실패했습니다.");
      return;
    }

    // self-lockout 경고가 있으면 먼저 보여주고, 닫은 뒤 리로드.
    if (data.warning) {
      await Modal.alert(data.warning);
    } else if (data.msg) {
      Toast.success(data.msg);
    }

    if (data.redirect) {
      window.location.href = data.redirect;
      return;
    }
    window.location.reload();
  } catch {
    restore();
    void Modal.alert("네트워크 오류가 발생했습니다.");
  }
}
